import React, { useMemo } from 'react'
import gsap from 'gsap'

import { BonusModal, BonusModalFooter } from '@components'

import { GameUIMessageType, LuckySpinsStartModalClosedMessage, ViewportType } from 'game-ui-types'
import { useConfigurationStore, useLuckySpinsStore } from '@stores'
import { useFormatMoney } from '@hooks'
import { useTranslation } from '@amusnet/translations/react'
import { GradientNumber } from '../'

import luckySpinsModalBodyBG from '@assets/lucky-spins/lucky-spins-modal-body-bg.png'
import luckySpinsModalHeaderBG from '@assets/lucky-spins/lucky-spins-modal-header-bg.png'
import luckySpinsPlayNow from '@assets/lucky-spins/lucky-spins-play-now.svg'
import LuckySpinsOutroContent from './LuckySpinsOutroContent'
import { CloverAnimation } from '@amusnet/react-ui'

const LuckySpins: React.FC = () => {
    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const currency = useConfigurationStore((state) => state.currency)

    const isModalOpened = useLuckySpinsStore((state) => state.isModalOpened)
    const setIsModalOpened = useLuckySpinsStore((state) => state.setIsModalOpened)
    const activeBet = useLuckySpinsStore((state) => state.activeBet)
    const remainingSpins = useLuckySpinsStore((state) => state.remainingSpins)
    const isIntro = useLuckySpinsStore((state) => state.isIntro)
    const setShowRemainingSpinsBanner = useLuckySpinsStore((state) => state.setShowRemainingSpinsBanner)
    const setAreLSAboutToStart = useLuckySpinsStore((state) => state.setAreLSAboutToStart)

    const viewport = useConfigurationStore((state) => state.viewport)

    const modalHeight =
        viewport === ViewportType.MOBILE_PORTRAIT
            ? '1060px'
            : viewport === ViewportType.MOBILE_LANDSCAPE
              ? '810px'
              : '835px'

    const wrapperRef = React.useRef<HTMLDivElement>(null)

    const handlePlayNowClick = () => {
        setShowRemainingSpinsBanner(true)

        if (isModalOpened && wrapperRef.current) {
            gsap.fromTo(
                wrapperRef.current,
                { scale: 1 },
                {
                    scale: 0,
                    duration: 0.3,
                    onComplete: () => {
                        setIsModalOpened(false)
                    },
                }
            )
        }

        const luckySpinsStartModalClosedMessage: LuckySpinsStartModalClosedMessage = {
            type: GameUIMessageType.LUCKY_SPINS_START_MODAL_CLOSED,
        }

        window.uiBus.postMessage(luckySpinsStartModalClosedMessage)

        // LS started once button clicked so they are not in still about to start phase - this will reset autoplay to act as autoplay not collect
        setAreLSAboutToStart(false)
    }

    const modalTitleStyles =
        'text-[60px] mb-1.5 tracking-[0] drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)] bg-gradient-to-b from-[#FFCB32] via-[#FFE95D] to-[#D5A703] text-transparent bg-clip-text'

    const renderIntroContent = useMemo(() => {
        return (
            <>
                <div
                    id="lucky-spins-intro"
                    className="relative w-[600px] z-20 mb-6.5 mx-auto py-5 leading-none font-bold text-center"
                >
                    <p className={modalTitleStyles}>{t('gameui.great')}</p>
                    <p className="text-[30px]">{t('gameui.bonusSystem.youHaveWon')}</p>

                    <div className="flex flex-col items-center justify-center h-[280px] mt-[-10px]">
                        <div className="absolute z-[-1]">
                            <CloverAnimation className="flex items-center justify-center">
                                <GradientNumber fontSize={120} yOffset={3}>
                                    {remainingSpins}
                                </GradientNumber>
                            </CloverAnimation>
                        </div>
                    </div>
                    <p className="text-[40px] tracking-[0] drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]">
                        {t('gameui.bonusSystem.luckySpin', undefined, +remainingSpins)}
                    </p>
                    <div className="flex justify-center text-[30px] mt-[15px] tracking-[0] text-[#FFE95D] drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]">
                        <p className="mr-[5px]">
                            {`${t('gameui.bonusSystem.onBet')} `}
                            {formatMoney(+activeBet)}
                        </p>
                        <p>{currency}</p>
                    </div>
                </div>

                <BonusModalFooter>
                    <button className="relative z-20 mt-[10px] mb-[20px]" onClick={handlePlayNowClick}>
                        <div className="relative">
                            <img src={luckySpinsPlayNow} alt="Buy Bonus button" className="mx-auto" />
                            <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-full text-white font-bold text-3xl drop-shadow-[0_3.2px_3.2px_rgba(0,0,0,0.5)]">
                                {t('gameui.playNow')}
                            </div>
                        </div>
                    </button>
                </BonusModalFooter>
            </>
        )
    }, [isIntro, isModalOpened, formatMoney])

    return (
        <BonusModal isOpen={isModalOpened} showCloseIcon={false} contentShadow={false}>
            <div ref={wrapperRef}>
                <div
                    style={{
                        height: isIntro ? modalHeight : 'auto',
                    }}
                >
                    <div className="h-[200px] mb-[-2px]">
                        <img src={luckySpinsModalHeaderBG} alt="Lucky Spins" />
                    </div>
                    <div className="relative flex flex-col items-center justify-center">
                        <img
                            src={luckySpinsModalBodyBG}
                            alt="Lucky Spins"
                            className="absolute w-full h-full rounded-bl-[20px] rounded-br-[20px] object-cover z-0"
                        />
                        <span className="absolute w-full rounded-bl-[20px] rounded-br-[20px] h-full top-0 left-0 z-10 bg-gradient-to-b from-[#00200A] to-[#083C1F]/10 shadow-[0_10px_10px_rgba(0,0,0,0.6)]"></span>
                        {isIntro ? renderIntroContent : <LuckySpinsOutroContent modalTitleStyles={modalTitleStyles} />}
                    </div>
                </div>
            </div>
        </BonusModal>
    )
}

export default LuckySpins
